<template lang="pug">
.form-group(id="dawnSelect__main-wrapper")
  ed-input-tooltip(v-if="helptipBody && isOpen || helptipBody && error"
                  :helptipHeader='helptipHeader'
                  :helptipBody='helptipBody'
                  :errorTip='error'
                  :class='{"error-message": error}'
                  )
  label.control-label(v-if="fieldLabel")
    | {{fieldLabel}}
    span(v-if="required") &nbsp;*
  .dawnSelect(
    :tabindex="searchable ? -1 : tabindex"
    :class="{ 'dawnSelect--active form-field--active': isOpen, 'dawnSelect--disabled': disabled, 'dawnSelect--above': isAbove }"
    @focus="activate()"
    @blur="searchable ? false : deactivate()"
    @keydown.self.down.prevent="pointerForward()"
    @keydown.self.up.prevent="pointerBackward()"
    @keydown.enter.tab.stop.self="addPointerElement($event)"
    @keyup.esc="deactivate()"
  )
    slot(name="caret", :toggle="toggle")
      .dawnSelect__select(@mousedown.prevent.stop="toggle()")
    slot(name="clear", :search="search")
    .dawnSelect__tags(ref="tags")
      template(v-if="internalValue && internalValue.length > limit")
        strong.dawnSelect__strong(v-text="limitText(internalValue.length - limit)")
      transition(name="dawnSelect__loading")
        slot(name="loading")
          .dawnSelect__spinner(v-show="loading")
      input.dawnSelect__input(
        @focus="focused = true"
        @blur="focused = false"
        ref="search"
        :name="name"
        :id="id"
        type="text"
        autocomplete="off"
        :placeholder="placeholder"
        v-bind="$props"
        v-focus.lazy="autofocus"
        :required="required"
        :autofocus="autofocus"
        v-if="searchable"
        :value="isOpen ? search : currentOptionLabel"
        :disabled="disabled"
        :tabindex="tabindex"
        @input="updateSearch($event.target.value)"
        @focus.prevent="activate()"
        @blur.prevent="deactivate()"
        @keyup.esc="deactivate()"
        @keydown.down.prevent="pointerForward()"
        @keydown.up.prevent="pointerBackward()"
        @keydown.enter.prevent.stop.self="addPointerElement($event)"
        @keydown.delete.stop="removeLastElement()"
      )
      span.dawnSelect__single(
        v-if="!searchable"
        @mousedown.prevent="toggle"
        v-text="currentOptionLabel"
      )
    transition(name="dawnSelect")
      .dawnSelect__content-wrapper(:style="{ maxHeight: optimizedHeight + 'px' }"
                                    v-show="isOpen"
                                    @focus="activate"
                                    @mousedown.prevent
                                    ref="list"
      )
        ul.dawnSelect__content(:style="contentStyle")
          slot(name="beforeList")
          li(v-if="multiple && max === internalValue.length")
            span.dawnSelect__option
              slot(name="maxElements") Maximum of {{ max }} options selected. First remove a selected option to select another.
          template(v-if="!max || internalValue.length < max")
            li.dawnSelect__element(v-for="(option, index) of filteredOptions" :key="index")
              span.dawnSelect__option(
                v-if="!(option && (option.$isLabel || option.$isDisabled))"
                :class="optionHighlight(index, option)"
                @click.stop="select(option)"
                @touchstart.stop.prevent="handleTouchStartStop(index, option)"
                @mouseenter.self="pointerSet(index)"
                :data-select="option && option.isTag ? tagPlaceholder : selectLabelText"
                :data-selected="selectedLabelText"
                :data-deselect="deselectLabelText"
              )
                slot(name="option", :option="option", :search="search")
                  span {{ getOptionLabel(option) }}
              span.dawnSelect__option.dawnSelect__option--disabled(
                v-if="option && (option.$isLabel || option.$isDisabled)"
                :class="optionHighlight(index, option)"
              )
                slot(name="option", :option="option", :search="search")
                  span {{ getOptionLabel(option) }}
          //-  This allows us to display a message if no results are found in search.
          //-  li(v-show="showNoResults && (filteredOptions.length === 0 && search && !loading)")
          //-    span.dawnSelect__option
          //-    slot(name="noResult") No items found. Consider changing your search query.
          slot(name="afterList")
  .dawnSelect__tags-wrap(v-show="visibleValue.length > 0")
    template(v-for="option of visibleValue", @mousedown.prevent)
      slot(name="tag", :option="option", :search="search", :remove="removeElement")
        span.dawnSelect__tag-icon
          span(v-text="getOptionLabel(option)")
          i.dawnSelect__tag-icon(aria-hidden="true", tabindex="1",
                                @keydown.enter.prevent="removeElement(option)",
                                @mousedown.prevent="removeElement(option)"
          )
</template>

<script>

import multiselectMixin from './multiselectMixin'
import pointerMixin from './pointerMixin'
import EdInputTooltip from '@/core/components/edInputTooltip/index.vue'

export default {
  name: 'ed-select',
  mixins: [multiselectMixin, pointerMixin],
  props: {
    required: {
      type: Boolean,
      default: false
    },
    autofocus: {
      type: Boolean,
      default: false
    },
    fieldLabel: {
      type: String,
      default: ''
    },
    helptipHeader: String,
    helptipBody: String,
    name: {
      type: String,
      default: ''
    },
    selectLabel: {
      type: String,
      default: 'Press enter to select'
    },
    selectedLabel: {
      type: String,
      default: 'Selected'
    },
    deselectLabel: {
      type: String,
      default: 'Press enter to remove'
    },
    showLabels: {
      type: Boolean,
      default: true
    },
    limit: {
      type: Number,
      default: 99999
    },
    maxHeight: {
      type: Number,
      default: 300
    },
    limitText: {
      type: Function,
      default: count => `and ${count} more`
    },
    loading: {
      type: Boolean,
      default: false
    },
    disabled: {
      type: Boolean,
      default: false
    },
    openDirection: {
      type: String,
      default: ''
    },
    showNoResults: {
      type: Boolean,
      default: true
    },
    tabindex: {
      type: Number,
      default: 0
    },
    error: Boolean
  },
  directives: {
    focus: {
      inserted: function(el, binding) {
        if (binding.value) el.focus()
        else el.blur()
      },
      componentUpdated: function(el, binding) {
        if (binding.modifiers.lazy) {
          if (Boolean(binding.value) === Boolean(binding.oldValue)) {
            return
          }
        }
        if (binding.value) el.focus()
        else el.blur()
      }
    }
  },
  components: {
    'ed-input-tooltip': EdInputTooltip
  },
  data: function() {
    return {
      focused: false
    }
  },
  computed: {
    visibleValue () {
      return this.multiple
        ? this.internalValue.slice(0, this.limit)
        : []
    },
    deselectLabelText () {
      return this.showLabels
        ? this.deselectLabel
        : ''
    },
    selectLabelText () {
      return this.showLabels
        ? this.selectLabel
        : ''
    },
    selectedLabelText () {
      return this.showLabels
        ? this.selectedLabel
        : ''
    },
    // inputStyle () {
    //  if (this.multiple && this.value && this.value.length) {
    //    // Hide input by setting the width to 0 allowing it to receive focus
    //    return this.isOpen ? { 'width': 'auto' } : { 'width': '0', 'position': 'absolute' }
    //  }
    // },
    contentStyle () {
      return this.options.length
        ? { 'display': 'inline-block' }
        : { 'display': 'block' }
    },
    isAbove () {
      if (this.openDirection === 'above' || this.openDirection === 'top') {
        return true
      } else if (this.openDirection === 'below' || this.openDirection === 'bottom') {
        return false
      } else {
        return this.prefferedOpenDirection === 'above'
      }
    }
  }
}
</script>

<style scoped lang='scss'>
@import "~@/assets/sass/_variables.scss";
@import "~@/assets/sass/_form_fields.scss";

body .theme .form-group {
  margin: 0;
  padding: 0;
}

.select-main {
  color: $primary-05;
  font-weight: $font-weight-bold;
  font-size: $font-size-small;
  margin: 0;
}

.select-secondary {
  color: $ink;
  font-weight: $font-weight-normal;
  margin: 0;
}

#dawnSelect__main-wrapper {
  position: relative;
}

fieldset[disabled] .dawnSelect {
  pointer-events: none;
}
.dawnSelect__spinner {
  position: absolute;
  right: 1px;
  top: 1px;
  width: 48px;
  height: 35px;
  background: #fff;
  display: block;
}
.dawnSelect__spinner:before,
.dawnSelect__spinner:after {
  position: absolute;
  content: '';
  top: 50%;
  left: 50%;
  margin: -8px 0 0 -8px;
  width: 16px;
  height: 16px;
  border-radius: 100%;
  border-color: #41B883 transparent transparent;
  border-style: solid;
  border-width: 2px;
  box-shadow: 0 0 0 1px transparent;
}
.dawnSelect__spinner:before {
  animation: spinning 2.4s cubic-bezier(0.41, 0.26, 0.2, 0.62);
  animation-iteration-count: infinite;
}
.dawnSelect__spinner:after {
  animation: spinning 2.4s cubic-bezier(0.51, 0.09, 0.21, 0.8);
  animation-iteration-count: infinite;
}
.dawnSelect__loading-enter-active,
.dawnSelect__loading-leave-active {
  transition: opacity 0.4s ease-in-out;
  opacity: 1;
}
.dawnSelect__loading-enter,
.dawnSelect__loading-leave-active {
  opacity: 0;
}
.dawnSelect,
.dawnSelect__input,
.dawnSelect__single {
  font-family: inherit;
  font-size: $font-size-base;
  touch-action: manipulation;
}
.dawnSelect {
  @include form-field;
  box-sizing: content-box;
  display: block;
  position: relative;
  width: 100%;
  padding: .15rem 0rem;
  color: $ink;
}
.dawnSelect * {
  box-sizing: border-box;
}
.dawnSelect:focus {
  /*outline: none;
  border: $border-width-default solid;
  border-color: $tertiary-01;
  box-shadow: 0px 0px 0px 1px $tertiary-01;*/
  input {
    outline:none;
  }
}
.dawnSelect--disabled {
  pointer-events: none;
  opacity: 0.6;
}
.dawnSelect--active {
  /*z-index: 50;
  border: $border-width-default solid;
  border-color: var(--tertiary-42);
  box-shadow: 0px 0px 0px 1px var(--tertiary-42);*/
}
.dawnSelect--active:not(.dawnSelect--above) .dawnSelect__current,
.dawnSelect--active:not(.dawnSelect--above) .dawnSelect__input,
.dawnSelect--active:not(.dawnSelect--above) .dawnSelect__tags {
  border-bottom-left-radius: 0;
  border-bottom-right-radius: 0;
}
.dawnSelect--active .dawnSelect__select:before {
  transform: rotateZ(180deg);
  display: inline-block;
  top: 0;
  transition: transform .2s ease-out;
}
.dawnSelect--above.dawnSelect--active .dawnSelect__current,
.dawnSelect--above.dawnSelect--active .dawnSelect__input,
.dawnSelect--above.dawnSelect--active .dawnSelect__tags {
  border-top-left-radius: 0;
  border-top-right-radius: 0;
}
.dawnSelect__input,
.dawnSelect__single {
  position: relative;
  display: inline-block;
  border: none;
  border-radius: $border-radius;
  padding: 0 0 0 .2rem;
  width: calc(100%);
  transition: border 0.1s ease;
  box-sizing: border-box;
  vertical-align: top;
  background: $canvas;
}
.dawnSelect__tag ~ .dawnSelect__input,
.dawnSelect__tag ~ .dawnSelect__single {
  width: auto;
}
.dawnSelect__input:hover,
.dawnSelect__single:hover {
  border-color: #cfcfcf;
}
.dawnSelect__input:focus,
.dawnSelect__single:focus {
  border-color: #a8a8a8;
  outline: none;
}
.dawnSelect__single {
  padding-left: 6px;
  margin-bottom: 8px;
}
.dawnSelect__tags-wrap {
  display: inline;
}
.dawnSelect__tags {
  display: block;
  padding: .3rem 3rem .25rem .6rem;
  background: $canvas;
}
.dawnSelect__tag {
  position: relative;
  display: inline-block;
  padding: 4px 26px 4px 10px;
  border-radius: 5px;
  margin-right: 10px;
  color: #fff;
  line-height: 1;
  background: #41B883;
  margin-bottom: 5px;
  white-space: nowrap;
  overflow: hidden;
  max-width: 100%;
  text-overflow: ellipsis;
}
.dawnSelect__tag-icon {
  cursor: pointer;
  margin-left: 7px;
  position: absolute;
  right: 0;
  top: 0;
  bottom: 0;
  font-weight: 700;
  font-style: initial;
  width: 22px;
  text-align: center;
  line-height: 22px;
  transition: all 0.2s ease;
  border-radius: 5px;
}
.dawnSelect__tag-icon:after {
  content: "×";
  color: #266d4d;
  font-size: $font-size-base;
}
.dawnSelect__tag-icon:focus,
.dawnSelect__tag-icon:hover {
  background: #369a6e;
}
.dawnSelect__tag-icon:focus:after,
.dawnSelect__tag-icon:hover:after {
  color: white;
}
.dawnSelect__current {
  line-height: 16px;
  min-height: 40px;
  box-sizing: border-box;
  display: block;
  overflow: hidden;
  padding: 8px 12px 0;
  padding-right: 30px;
  white-space: nowrap;
  margin: 0;
  text-decoration: none;
  border-radius: 5px;
  border: 1px solid #E8E8E8;
  cursor: pointer;
}
.form-group.inline .dawnSelect__select {
  line-height: 90%;
}
.dawnSelect__select {
  //line-height: 90%;
  display: block;
  position: absolute;
  box-sizing: border-box;
  width: 40px;
  height: 90%;
  right: 1px;
  top: 1px;
  padding: 4px 8px;
  margin: 0;
  text-decoration: none;
  text-align: center;
  cursor: pointer;
  transition: transform 0.2s ease;
}
.dawnSelect__select:before {
  position: relative;
  right: 0;
  top: 0;
  margin-top: 4px;
  border-style: solid;
  border-width: 5px 5px 0 5px;
  color: getThemifyVariable('primary-05');
  border-color: $primary-05 transparent transparent transparent;
  content: '';
  transform: rotateZ(0deg);
  display: inline-block;
  transition: transform .2s ease-out;
}
.dawnSelect__placeholder {
  color: #ADADAD;
  display: inline-block;
  margin-bottom: 10px;
  padding-top: 2px;
}
.dawnSelect--active .dawnSelect__placeholder {
  display: none;
}
.dawnSelect__content-wrapper {
  position: absolute;
  display: block;
  background: $canvas;
  width: 100%;
  max-height: 240px;
  overflow-y: auto;
  border: $border-width-default solid;
  border-color: $background;
  z-index: 50;
  -webkit-overflow-scrolling: touch;
  margin-top: 4px;
  overflow-x: hidden;
}

.dawnSelect__content-wrapper::-webkit-scrollbar-track {
  border-radius: 10px;
  background-color: transparent;
  padding-right: 2px;
}
.dawnSelect__content-wrapper::-webkit-scrollbar {
  width: 6px;
  background-color: transparent;
}
.dawnSelect__content-wrapper::-webkit-scrollbar-thumb{
  border-radius: 10px;
  background-color: $background;
}
.dawnSelect__content {
  list-style: none;
  display: inline-block;
  padding: 0;
  margin: 0;
  min-width: 100%;
  vertical-align: top;
}
.dawnSelect--above .dawnSelect__content-wrapper {
  bottom: 100%;
  border-bottom-left-radius: 0;
  border-bottom-right-radius: 0;
  border-top-left-radius: 5px;
  border-top-right-radius: 5px;
  border-bottom: none;
  border-top: 1px solid #E8E8E8;
}
.dawnSelect__content::webkit-scrollbar {
  display: none;
}
.dawnSelect__element {
  display: block;
  font-size: $font-size-small;
}

.dawnSelect__element:hover {
  background: $background-light;
}
.dawnSelect__option {
  display: block;
  padding: 8px 16px;
  min-height: 24px;
  line-height: 16px;
  text-decoration: none;
  text-transform: none;
  vertical-align: middle;
  position: relative;
  cursor: pointer;
  white-space: nowrap;
  font-size: $font-size-small;
}
.dawnSelect__option:after {
  top: 0;
  right: 0;
  position: absolute;
  line-height: 40px;
  padding-right: 12px;
  padding-left: 20px;
}
.dawnSelect__option--highlight {
  background: #41B883;
  outline: none;
  color: white;
}
.dawnSelect__option--highlight:after {
  content: attr(data-select);
  background: #41B883;
  color: white;
}
.dawnSelect__option--selected {
  background: #F3F3F3;
  color: #35495E;
  font-weight: bold;
}
.dawnSelect__option--selected:after {
  content: attr(data-selected);
  color: silver;
}
.dawnSelect__option--selected.dawnSelect__option--highlight {
  background: #FF6A6A;
  color: #fff;
}
.dawnSelect__option--selected.dawnSelect__option--highlight:after {
  background: #FF6A6A;
  content: attr(data-deselect);
  color: #fff;
}
.dawnSelect--disabled {
  background: #ededed;
  pointer-events: none;
}
.dawnSelect--disabled .dawnSelect__current,
.dawnSelect--disabled .dawnSelect__select {
  background: #ededed;
  color: #a6a6a6;
}
.dawnSelect__option--disabled {
  background: #ededed;
  color: #a6a6a6;
  cursor: text;
  pointer-events: none;
}
.dawnSelect__option--disabled.dawnSelect__option--highlight {
  background: #dedede !important;
}
.dawnSelect-enter-active,
.dawnSelect-leave-active {
  transition: all 0.15s ease;
}
.dawnSelect-enter,
.dawnSelect-leave-active {
  opacity: 0;
}
.dawnSelect__strong {
  margin-bottom: 8px;
  line-height: 20px;
  display: inline-block;
  vertical-align: top;
}
*[dir="rtl"] .dawnSelect {
    text-align: right;
}
*[dir="rtl"] .dawnSelect__select {
    right: auto;
    left: 1px;
}
*[dir="rtl"] .dawnSelect__tags {
    padding: 8px 8px 0px 40px;
}
*[dir="rtl"] .dawnSelect__content {
    text-align: right;
}
*[dir="rtl"] .dawnSelect__option:after {
    right: auto;
    left: 0;
}
*[dir="rtl"] .dawnSelect__clear {
    right: auto;
    left: 12px;
}
*[dir="rtl"] .dawnSelect__spinner {
    right: auto;
    left: 1px;
}
@keyframes spinning {
  from { transform:rotate(0) }
  to { transform:rotate(2turn) }
}

.dawnSelect__content-table-wrapper {
  display: table;
  width: 100%;
}
.dawnSelect__content-table-row-wrapper {
  display: table-row;
}

.dawnSelect__content-table-body-wrapper {
  display: table-row-group;
}
.dawnSelect__content-table-image-wrapper {
  width: 70px;
  display: table-cell;
  height: 60px;
  padding: 0px 0px 0px 10px;
}

.dawnSelect__content-table-image-wrapper img {
  width:100%;
}
.dawnSelect__content-table-content-wrapper {
  vertical-align: top;
  display: table-cell;
  padding: 0px 10px;
}

// Small inline
.form-group.inline {
  display: inline-block;

  .dawnSelect {
    padding-top: 0px;
    padding-bottom: 0px;
    width: auto;
    display: inline-block;
    border-color: $background;
    border-radius: 3em;
    background: transparent;

    .dawnSelect__tags {
      padding-top: 1px;
      padding-bottom: 1px;
      background: transparent;
      input.dawnSelect__input {
        background: transparent;
      }
    }
  }
}

// Widget Select Styles
.widget-select {
  .dawnSelect {
    border-color: $primary-03;
    border-radius: 3em;
    background: transparent;
  }

  .dawnSelect__tags {
    background: transparent;
    input.dawnSelect__input {
      background: transparent;
      color: $canvas;
      @include placeholder {
        color: $canvas;
      }
    }
  }

  .dawnSelect__select {
    &:before {
      color: $primary-03;
      border-color: $primary-03 transparent transparent transparent;
    }
  }

}

</style>



// WEBPACK FOOTER //
// index.vue?5689f438